import React, { useState } from 'react';
import { Table, Tag, Button, Space, Popconfirm, Tooltip, Progress, Input } from 'antd';
import {
    EditOutlined,
    DeleteOutlined,
    DollarOutlined,
    ExclamationCircleOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
} from '@ant-design/icons';
import { formatCurrency, formatDate } from '../../../../shared/utils/formatters';
import { useDebounce } from '../../../../shared/hooks/useDebounce';
import dayjs from 'dayjs';
import styles from './DebtList.module.css';

const { Search } = Input;


export default function DebtList({
    debts,
    loading,
    onEdit,
    onDelete,
    onPayment
}) {
    const [searchText, setSearchText] = useState('');
    const debouncedSearchText = useDebounce(searchText, 300);

    const filteredDebts = debts.filter(debt => {
        if (!debouncedSearchText) return true;

        const searchLower = debouncedSearchText.toLowerCase();
        return (
            debt.nguoiVay.toLowerCase().includes(searchLower) ||
            debt.mucDich.toLowerCase().includes(searchLower) ||
            debt.soTien.toString().includes(searchLower)
        );
    });

    const getDebtStatus = (debt) => {
        const paidAmount = debt.soTienDaTra || 0;
        const totalAmount = debt.soTien;
        const paidPercentage = (paidAmount / totalAmount) * 100;

        if (debt.daTra || paidPercentage >= 100) {
            return {
                status: 'success',
                text: 'Đã trả hết',
                icon: <CheckCircleOutlined />
            };
        }

        if (paidAmount > 0) {
            return {
                status: 'processing',
                text: `Đã trả ${Math.round(paidPercentage)}%`,
                icon: <ClockCircleOutlined />
            };
        }

        const isOverdue = dayjs(debt.ngayTra).isBefore(dayjs(), 'day');
        if (isOverdue) {
            return {
                status: 'error',
                text: 'Quá hạn',
                icon: <ExclamationCircleOutlined />
            };
        }

        return {
            status: 'warning',
            text: 'Chưa trả',
            icon: <ExclamationCircleOutlined />
        };
    };

    const columns = [
        {
            title: 'Ngày vay',
            dataIndex: 'ngayVay',
            key: 'ngayVay',
            render: (date) => formatDate(date),
            sorter: (a, b) => new Date(a.ngayVay) - new Date(b.ngayVay),
            width: 120,
        },
        {
            title: 'Ngày trả',
            dataIndex: 'ngayTra',
            key: 'ngayTra',
            render: (date, record) => {
                const isOverdue = !record.daTra && dayjs(date).isBefore(dayjs(), 'day');
                return (
                    <span style={{ color: isOverdue ? '#ff4d4f' : 'inherit' }}>
                        {formatDate(date)}
                        {isOverdue && (
                            <ExclamationCircleOutlined
                                style={{ marginLeft: 4, color: '#ff4d4f' }}
                            />
                        )}
                    </span>
                );
            },
            sorter: (a, b) => new Date(a.ngayTra) - new Date(b.ngayTra),
            width: 120,
        },
        {
            title: 'Người cho vay',
            dataIndex: 'nguoiVay',
            key: 'nguoiVay',
            width: 150,
            ellipsis: true,
        },
        {
            title: 'Số tiền',
            dataIndex: 'soTien',
            key: 'soTien',
            render: (amount) => (
                <span className={styles.amount}>
                    {formatCurrency(amount)}
                </span>
            ),
            sorter: (a, b) => a.soTien - b.soTien,
            width: 130,
        },
        {
            title: 'Đã trả',
            dataIndex: 'soTienDaTra',
            key: 'soTienDaTra',
            render: (paid, record) => {
                const paidAmount = paid || 0;
                const percentage = (paidAmount / record.soTien) * 100;
                return (
                    <div className={styles.paymentProgress}>
                        <div className={styles.paymentAmount}>
                            {formatCurrency(paidAmount)}
                        </div>
                        <Progress
                            percent={Math.round(percentage)}
                            size="small"
                            status={percentage === 100 ? 'success' : 'active'}
                            strokeColor={percentage === 100 ? '#52c41a' : '#1890ff'}
                        />
                    </div>
                );
            },
            width: 150,
        },
        {
            title: 'Còn lại',
            key: 'remaining',
            render: (_, record) => {
                const remaining = record.soTien - (record.soTienDaTra || 0);
                return (
                    <span
                        style={{
                            color: remaining > 0 ? '#ff4d4f' : '#52c41a',
                            fontWeight: 'bold'
                        }}
                    >
                        {formatCurrency(remaining)}
                    </span>
                );
            },
            sorter: (a, b) => {
                const remainingA = a.soTien - (a.soTienDaTra || 0);
                const remainingB = b.soTien - (b.soTienDaTra || 0);
                return remainingA - remainingB;
            },
            width: 130,
        },
        {
            title: 'Trạng thái',
            dataIndex: 'daTra',
            key: 'status',
            render: (_, record) => {
                const status = getDebtStatus(record);
                return (
                    <Tag color={status.status} icon={status.icon}>
                        {status.text}
                    </Tag>
                );
            },
            filters: [
                { text: 'Đã trả hết', value: 'paid' },
                { text: 'Đã trả một phần', value: 'partial' },
                { text: 'Chưa trả', value: 'unpaid' },
                { text: 'Quá hạn', value: 'overdue' },
            ],
            onFilter: (value, record) => {
                const paidAmount = record.soTienDaTra || 0;
                const paidPercentage = (paidAmount / record.soTien) * 100;

                if (value === 'paid') return record.daTra || paidPercentage >= 100;
                if (value === 'partial') return paidAmount > 0 && paidPercentage < 100 && !record.daTra;
                if (value === 'unpaid') return paidAmount === 0 && !dayjs(record.ngayTra).isBefore(dayjs(), 'day');
                if (value === 'overdue') return !record.daTra && paidPercentage < 100 && dayjs(record.ngayTra).isBefore(dayjs(), 'day');
                return true;
            },
            width: 120,
        },
        {
            title: 'Mục đích',
            dataIndex: 'mucDich',
            key: 'mucDich',
            ellipsis: {
                showTitle: false,
            },
            render: (text) => (
                <Tooltip placement="topLeft" title={text}>
                    {text}
                </Tooltip>
            ),
            width: 200,
        },
        {
            title: 'Hành động',
            key: 'actions',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Thanh toán">
                        <Button
                            icon={<DollarOutlined />}
                            onClick={() => onPayment(record)}
                            size="small"
                            disabled={record.daTra}
                            type="primary"
                            className={styles.actionButton}
                        />
                    </Tooltip>

                    <Tooltip title="Chỉnh sửa">
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => onEdit(record)}
                            size="small"
                            className={styles.actionButton}
                        />
                    </Tooltip>

                    <Popconfirm
                        title="Xác nhận xóa"
                        description="Bạn có chắc muốn xóa khoản nợ này?"
                        onConfirm={() => onDelete(record.id)}
                        okText="Có"
                        cancelText="Không"
                        placement="topRight"
                    >
                        <Tooltip title="Xóa">
                            <Button
                                icon={<DeleteOutlined />}
                                danger
                                size="small"
                                className={styles.actionButton}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
            width: 120,
            fixed: 'right',
        },
    ];

    return (
        <div className={styles.debtListContainer}>
            <div className={styles.tableHeader}>
                <h3>📋 Danh sách nợ</h3>
                <Search
                    placeholder="Tìm kiếm theo tên, mục đích hoặc số tiền..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 300 }}
                    allowClear
                />
            </div>

            <Table
                columns={columns}
                dataSource={filteredDebts}
                rowKey="id"
                loading={loading}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) =>
                        `${range[0]}-${range[1]} của ${total} khoản nợ`,
                    pageSizeOptions: ['10', '20', '50'],
                }}
                rowClassName={(record) => {
                    if (record.daTra) return styles.paidRow;
                    if (dayjs(record.ngayTra).isBefore(dayjs(), 'day')) return styles.overdueRow;
                    return styles.unpaidRow;
                }}
                scroll={{ x: 1200 }}
                size="middle"
            />
        </div>
    );
}
